import React, { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaCog, FaQrcode, FaTrash, FaTimes } from 'react-icons/fa';
import AssignBuildingModal from '../modals/AssignBuildingModal';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';
import QRCodeModal from '../../../components/modals/QRCodeModal';

const BuildingEntranceTab = () => {
  const { t } = useTranslation();
  const [buildings, setBuildings] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [lockFilter, setLockFilter] = useState('all');
  const [webrtcFilter, setWebrtcFilter] = useState('all');
  const [communities, setCommunities] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [displayBuildings, setDisplayBuildings] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  // Fetch communities on mount
  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get('/communities');
      setCommunities(response.data.communities);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBuildingEntrances();
  }, []);

  const fetchBuildingEntrances = async () => {
    try {
      const response = await axios.get('/settings/building-sip-settings');
      setBuildings(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveSip = async building => {
    try {
      await axios.post(`/settings/remove-building-sip`, {
        buildingId: building._id,
      });
      fetchBuildingEntrances();
    } catch (error) {
      console.error(error);
    }
  };

  // const displayBuildings = buildings.filter(building => {
  //   const communityMatch = selectedCommunity && building.communityId === selectedCommunity;

  //   const lockMatch = lockFilter === 'all' ? true :
  //     lockFilter === 'assigned' ? building.lockId :
  //     lockFilter === 'unassigned' ? !building.lockId : true;

  //   const webrtcMatch = webrtcFilter === 'all' ? true :
  //     webrtcFilter === 'assigned' ? building.sipAccount :
  //     webrtcFilter === 'unassigned' ? !building.sipAccount : true;

  //   return communityMatch && lockMatch && webrtcMatch;
  // });

  const handleQRCode = entrance => {
    setSelectedList(entrance);
    setIsQRCodeModalOpen(true);
  };

  useEffect(() => {
    setDisplayBuildings(
      buildings.filter(building => {
        const communityMatch = selectedCommunity && building.communityId === selectedCommunity;
        const lockMatch =
          lockFilter === 'all'
            ? true
            : lockFilter === 'assigned'
              ? building.lockId
              : lockFilter === 'unassigned'
                ? !building.lockId
                : true;
        const webrtcMatch =
          webrtcFilter === 'all'
            ? true
            : webrtcFilter === 'assigned'
              ? building.sipAccount
              : webrtcFilter === 'unassigned'
                ? !building.sipAccount
                : true;
        const matchesSearch =
          searchKey.toLowerCase().trim() === '' ||
          (building?.communityName &&
            building.communityName.toLowerCase().includes(searchKey.toLowerCase())) ||
          (building?.buildingName &&
            building.buildingName.toLowerCase().includes(searchKey.toLowerCase())) ||
          (building?.lockId &&
            building.lockId.toString().toLowerCase().includes(searchKey.toLowerCase())) ||
          (building?.sipAccount &&
            building.sipAccount.toLowerCase().includes(searchKey.toLowerCase()));
        return communityMatch && lockMatch && webrtcMatch && matchesSearch;
      })
    );
  }, [buildings, selectedCommunity, lockFilter, webrtcFilter, searchKey]);

  return (
    <div className="h-full">
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-skin-primary px-4 rounded-xl shadow-lg h-filter">
        <select
          value={selectedCommunity}
          onChange={e => setSelectedCommunity(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-filter-7"
        >
          <option value="" hidden>
            {t('Community List')}
          </option>
          {communities.map(community => (
            <option key={community._id} value={community._id}>
              {community.communityName}
            </option>
          ))}
        </select>

        <select
          value={lockFilter}
          onChange={e => setLockFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">{t('All Locks')}</option>
          <option value="assigned">{t('Lock Assigned')}</option>
          <option value="unassigned">{t('Lock Unassigned')}</option>
        </select>

        <select
          value={webrtcFilter}
          onChange={e => setWebrtcFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">{t('All WebRTC')}</option>
          <option value="assigned">{t('WebRTC Assigned')}</option>
          <option value="unassigned">{t('WebRTC Unassigned')}</option>
        </select>

        <div className="relative flex-1">
          <input
            type="text"
            placeholder={t('Search')}
            className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors w-full"
            value={searchKey}
            onChange={e => setSearchKey(e.target.value)}
          />
          {searchKey && (
            <button
              onClick={() => {
                setSearchKey('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="overflow-auto h-table-height">
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('No.')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Community Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Building Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Lock UUID')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('WebRTC Account')}
              </th>
              <th className="px-6 py-3 text-sm font-semibold text-skin-base text-right">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayBuildings.map((building, index) => (
              <tr key={building._id} className="hover:bg-gray-50">
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                  {building.communityName}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                  {building.buildingName}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                  {building.lockId}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                  {building.sipAccount}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2 justify-end">
                    {building.sipAccount && (
                      <button
                        onClick={() => {
                          setSelectedList(building);
                          setIsConfirmOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedList(building);
                        setIsAssignModalOpen(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <FaCog className="w-4 h-4" />
                    </button>
                    {building.sipAccount && (
                      <button
                        onClick={() => handleQRCode(building)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaQrcode className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modal */}
      <AssignBuildingModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedList(null);
        }}
        building={selectedList}
        onSuccess={() => {
          fetchBuildingEntrances();
          setIsAssignModalOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setSelectedList(null);
        }}
        onConfirm={() => handleRemoveSip(selectedList)}
        title={t('Remove SIP Account')}
        message={t('Are you sure you want to remove this SIP account?')}
        confirmText={t('Remove')}
        cancelText={t('Cancel')}
        type="danger"
      />

      <QRCodeModal
        isOpen={isQRCodeModalOpen}
        onClose={() => setIsQRCodeModalOpen(false)}
        qrValue={`${window.location.protocol}//${window.location.host}/visitor?type=02&lockId=${selectedList?.lockId}`}
        title={t('Building QR Code')}
      />
    </div>
  );
};

export default BuildingEntranceTab;
